import React, { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import HeroBackground from './HeroBackground'

interface HeroSectionProps {
  isMobile: boolean
  isScrolled: boolean
  isInDarkSection: boolean
  researchQuery: string
  displayedPlaceholder: string
  isTransitioning: boolean
  isPublic: boolean
  queryValid: boolean
  shouldAutoScrollHeroCards: boolean
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>
  onHome: () => void
  onLogin: () => void
  onGetStarted: () => void
  onResearchQueryChange: (value: string) => void
  onSelectPrivate: () => void
  onSelectPublic: () => void
  onStartResearch: () => void
}

interface ResearchCategory {
  id: string
  title: string
  description: string
  icon: React.ReactNode
}

interface CategoryCardProps extends ResearchCategory {
  className?: string
  ariaHidden?: boolean
  testId?: string
}

interface HeroCarouselSlide {
  id: string
  categories: ResearchCategory[]
  logicalIndex: number
  isClone?: boolean
}

const RESEARCH_CATEGORIES: ResearchCategory[] = [
  {
    id: 'investment',
    title: 'Investment Diligence',
    description: 'Company deep dives, valuation work, and deal screens.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M7 16l4-4 4 4 4-8" />
      </svg>
    ),
  },
  {
    id: 'competitive',
    title: 'Competitive Intelligence',
    description: 'Competitor teardowns, market maps, and positioning.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 17H5a2 2 0 00-2 2v0a2 2 0 002 2h4M9 17V9a2 2 0 00-2-2H5a2 2 0 00-2 2v0" />
        <path d="M14 17h4a2 2 0 012 2v0a2 2 0 01-2 2h-4M14 17V5a2 2 0 00-2-2h0a2 2 0 00-2 2v12" />
      </svg>
    ),
  },
  {
    id: 'realestate',
    title: 'Real Estate & Markets',
    description: 'Submarket comps, pipelines, and relocation economics.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    id: 'policy',
    title: 'Policy & Regulatory Risk',
    description: 'Track regulation, compliance, and approval paths.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6l9-3 9 3v6c0 5.25-3.75 10.15-9 11.25C6.75 22.15 3 17.25 3 12V6z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: 'economic',
    title: 'Market & Economic Impact',
    description: 'See how events or moves reshape a market.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
      </svg>
    ),
  },
  {
    id: 'biotech',
    title: 'Biotech & Pharma',
    description: 'Pipeline reads, trial tracking, and pricing risk.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 3H5v4l7 7-4 4 1.5 1.5L14 14l5 5 1.5-1.5L16 14l4-4V5h-4L9 3z" />
        <path d="M9 3l6 6" />
      </svg>
    ),
  },
]

const CategoryCard = ({ title, description, icon, className, ariaHidden, testId }: CategoryCardProps) => (
  <div
    className={className ? `research-category-card ${className}` : 'research-category-card'}
    aria-hidden={ariaHidden}
    data-testid={testId}
  >
    <div className="research-category-icon">{icon}</div>
    <p className="research-category-title">{title}</p>
    <p className="research-category-desc">{description}</p>
  </div>
)

const MOBILE_CAROUSEL_SLIDE_SIZE = 2
const MOBILE_CAROUSEL_AUTO_ADVANCE_MS = 4200
const MOBILE_CAROUSEL_PAUSE_MS = 5200
const MOBILE_CAROUSEL_SWIPE_THRESHOLD = 28

export default function HeroSection({
  isMobile,
  isScrolled,
  isInDarkSection,
  researchQuery,
  displayedPlaceholder,
  isTransitioning,
  isPublic,
  queryValid,
  shouldAutoScrollHeroCards,
  textareaRef,
  onHome,
  onLogin,
  onGetStarted,
  onResearchQueryChange,
  onSelectPrivate,
  onSelectPublic,
  onStartResearch,
}: HeroSectionProps) {
  const navSurfaceClass = isScrolled
    ? isInDarkSection
      ? 'bg-slate-950/72 border-b border-white/12 shadow-[0_12px_36px_rgba(15,23,42,0.28)] backdrop-blur-xl'
      : 'bg-white/74 border-b border-black/5 backdrop-blur-xl shadow-[0_8px_30px_rgba(15,23,42,0.05)]'
    : 'bg-transparent'
  const navTextClass = isInDarkSection && isScrolled ? 'text-white/80 hover:text-white' : 'text-slate-900 hover:text-slate-700'
  const visibilityHelperCopy = isPublic
    ? (isMobile ? 'Public runs appear on Explore.' : 'Runs marked public will appear on Explore and cannot be reverted.')
    : (isMobile ? 'Only you can see this run.' : 'Your research will be private and only visible to you.')
  const mobileHeroCarouselTrackRef = useRef<HTMLDivElement | null>(null)
  const mobileHeroCarouselTouchStartRef = useRef<number | null>(null)
  const mobileHeroCarouselPauseUntilRef = useRef(0)
  const mobileHeroCarouselEnableTransitionFrameRef = useRef<number | null>(null)
  const mobileHeroCarouselSlides = useMemo<HeroCarouselSlide[]>(() => {
    const slides: HeroCarouselSlide[] = []

    for (let index = 0; index < RESEARCH_CATEGORIES.length; index += MOBILE_CAROUSEL_SLIDE_SIZE) {
      slides.push({
        id: `hero-slide-${index / MOBILE_CAROUSEL_SLIDE_SIZE}`,
        categories: RESEARCH_CATEGORIES.slice(index, index + MOBILE_CAROUSEL_SLIDE_SIZE),
        logicalIndex: index / MOBILE_CAROUSEL_SLIDE_SIZE,
      })
    }

    return slides
  }, [])
  const mobileHeroLoopedSlides = useMemo<HeroCarouselSlide[]>(() => {
    if (mobileHeroCarouselSlides.length === 0) return []

    const firstSlide = mobileHeroCarouselSlides[0]
    const lastSlide = mobileHeroCarouselSlides[mobileHeroCarouselSlides.length - 1]

    return [
      { ...lastSlide, id: `${lastSlide.id}-clone-start`, isClone: true },
      ...mobileHeroCarouselSlides,
      { ...firstSlide, id: `${firstSlide.id}-clone-end`, isClone: true },
    ]
  }, [mobileHeroCarouselSlides])
  const [mobileHeroCarouselSlideIndex, setMobileHeroCarouselSlideIndex] = useState(1)
  const [mobileHeroCarouselActiveSlide, setMobileHeroCarouselActiveSlide] = useState(0)
  const [mobileHeroCarouselTransitionsEnabled, setMobileHeroCarouselTransitionsEnabled] = useState(true)
  const pauseMobileHeroCarousel = useCallback(() => {
    mobileHeroCarouselPauseUntilRef.current = Date.now() + MOBILE_CAROUSEL_PAUSE_MS
  }, [])
  const updateMobileHeroCarouselSlide = useCallback(
    (nextSlideIndex: number, shouldPause: boolean) => {
      if (shouldPause) pauseMobileHeroCarousel()

      setMobileHeroCarouselTransitionsEnabled(true)
      setMobileHeroCarouselSlideIndex(nextSlideIndex)

      if (nextSlideIndex <= 0) {
        setMobileHeroCarouselActiveSlide(mobileHeroCarouselSlides.length - 1)
        return
      }

      if (nextSlideIndex >= mobileHeroLoopedSlides.length - 1) {
        setMobileHeroCarouselActiveSlide(0)
        return
      }

      setMobileHeroCarouselActiveSlide(nextSlideIndex - 1)
    },
    [mobileHeroCarouselSlides.length, mobileHeroLoopedSlides.length, pauseMobileHeroCarousel]
  )

  useEffect(() => {
    setMobileHeroCarouselSlideIndex(1)
    setMobileHeroCarouselActiveSlide(0)
    setMobileHeroCarouselTransitionsEnabled(true)
  }, [isMobile])

  useEffect(
    () => () => {
      if (mobileHeroCarouselEnableTransitionFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileHeroCarouselEnableTransitionFrameRef.current)
      }
    },
    []
  )

  useEffect(() => {
    if (!isMobile || !shouldAutoScrollHeroCards || mobileHeroCarouselSlides.length <= 1) return

    const intervalId = window.setInterval(() => {
      if (mobileHeroCarouselPauseUntilRef.current > Date.now()) return

      setMobileHeroCarouselTransitionsEnabled(true)
      setMobileHeroCarouselSlideIndex(currentIndex => {
        const nextIndex = currentIndex + 1

        if (nextIndex >= mobileHeroLoopedSlides.length - 1) {
          setMobileHeroCarouselActiveSlide(0)
        } else {
          setMobileHeroCarouselActiveSlide(Math.max(0, nextIndex - 1))
        }

        return nextIndex
      })
    }, MOBILE_CAROUSEL_AUTO_ADVANCE_MS)

    return () => window.clearInterval(intervalId)
  }, [isMobile, mobileHeroCarouselSlides.length, mobileHeroLoopedSlides.length, shouldAutoScrollHeroCards])

  const handleMobileHeroCarouselTransitionEnd = useCallback(() => {
    if (mobileHeroLoopedSlides.length <= 1) return

    if (mobileHeroCarouselSlideIndex === 0) {
      setMobileHeroCarouselTransitionsEnabled(false)
      setMobileHeroCarouselSlideIndex(mobileHeroCarouselSlides.length)
      setMobileHeroCarouselActiveSlide(mobileHeroCarouselSlides.length - 1)
      mobileHeroCarouselEnableTransitionFrameRef.current = window.requestAnimationFrame(() => {
        setMobileHeroCarouselTransitionsEnabled(true)
      })
      return
    }

    if (mobileHeroCarouselSlideIndex === mobileHeroLoopedSlides.length - 1) {
      setMobileHeroCarouselTransitionsEnabled(false)
      setMobileHeroCarouselSlideIndex(1)
      setMobileHeroCarouselActiveSlide(0)
      mobileHeroCarouselEnableTransitionFrameRef.current = window.requestAnimationFrame(() => {
        setMobileHeroCarouselTransitionsEnabled(true)
      })
    }
  }, [mobileHeroCarouselSlideIndex, mobileHeroCarouselSlides.length, mobileHeroLoopedSlides.length])

  const handleMobileHeroCarouselTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    mobileHeroCarouselTouchStartRef.current = event.touches[0]?.clientX ?? null
    pauseMobileHeroCarousel()
  }, [pauseMobileHeroCarousel])

  const handleMobileHeroCarouselTouchEnd = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const touchStartX = mobileHeroCarouselTouchStartRef.current
    mobileHeroCarouselTouchStartRef.current = null

    if (touchStartX === null) return

    const deltaX = event.changedTouches[0]?.clientX - touchStartX

    if (Math.abs(deltaX) < MOBILE_CAROUSEL_SWIPE_THRESHOLD) return

    if (deltaX < 0) {
      updateMobileHeroCarouselSlide(mobileHeroCarouselSlideIndex + 1, false)
      return
    }

    updateMobileHeroCarouselSlide(mobileHeroCarouselSlideIndex - 1, false)
  }, [mobileHeroCarouselSlideIndex, updateMobileHeroCarouselSlide])

  const heroResearchCards = (
    <>
      <p className={`hero-field-label ${isMobile ? 'mb-3' : 'mb-4 mt-8'}`}>
        Great for research like this
      </p>
      {isMobile ? (
        <div className="hero-category-carousel" data-testid="hero-mobile-carousel">
          <div
            className={`hero-category-carousel-track hero-category-carousel-track--slides ${
              mobileHeroCarouselTransitionsEnabled ? 'hero-category-carousel-track--animated' : 'hero-category-carousel-track--static'
            }`}
            data-testid="hero-mobile-carousel-track"
            ref={mobileHeroCarouselTrackRef}
            onTouchStart={handleMobileHeroCarouselTouchStart}
            onTouchEnd={handleMobileHeroCarouselTouchEnd}
            onPointerDown={pauseMobileHeroCarousel}
            onTransitionEnd={event => {
              if (event.target !== event.currentTarget) return
              handleMobileHeroCarouselTransitionEnd()
            }}
            style={{
              transform: `translate3d(-${mobileHeroCarouselSlideIndex * 100}%, 0, 0)`,
            }}
          >
            {mobileHeroLoopedSlides.map(slide => (
              <div
                key={slide.id}
                className="hero-category-carousel-slide"
                aria-hidden={slide.isClone}
              >
                {slide.categories.map(category => (
                  <CategoryCard
                    key={`${slide.id}-${category.id}`}
                    {...category}
                    className="research-category-card--carousel-page"
                    ariaHidden={slide.isClone}
                    testId={slide.isClone ? undefined : 'hero-mobile-carousel-card'}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="hero-category-carousel-dots" data-testid="hero-mobile-carousel-dots">
            {mobileHeroCarouselSlides.map(slide => {
              const isActive = slide.logicalIndex === mobileHeroCarouselActiveSlide

              return (
                <button
                  key={slide.id}
                  type="button"
                  className={`hero-category-carousel-dot ${isActive ? 'hero-category-carousel-dot--active' : ''}`}
                  aria-label={`Show research cards ${slide.logicalIndex + 1}`}
                  aria-current={isActive ? 'true' : undefined}
                  data-testid="hero-mobile-carousel-dot"
                  onClick={() => updateMobileHeroCarouselSlide(slide.logicalIndex + 1, true)}
                />
              )
            })}
          </div>
        </div>
      ) : (
        <div className="hero-category-grid">
          {RESEARCH_CATEGORIES.map(category => (
            <CategoryCard key={category.id} {...category} />
          ))}
        </div>
      )}
    </>
  )

  const heroActionPane = (
    <div
      className={`hero-action-pane ${
        isMobile ? 'hero-action-pane--mobile px-4 pb-4' : 'border-l border-slate-200/80 px-8 pb-8 pt-8 lg:px-10 lg:pb-10 lg:pt-10 xl:px-12 xl:pb-12 xl:pt-12'
      }`}
    >
      <div className={`hero-card-inner ${isMobile ? 'space-y-3' : 'space-y-5'}`}>
        <div className={isMobile ? 'space-y-2.5' : 'space-y-4'}>
          <div>
            <p className={`${isMobile ? 'hero-field-label mb-1.5' : 'hero-field-label mb-3'}`}>Research question</p>
            <div className="hero-query-shell relative rounded-[22px] border border-black bg-white transition focus-within:border-black">
              {researchQuery.length === 0 && (
                <div
                  className={`absolute pointer-events-none ${isMobile ? 'px-4 py-3.5 text-[0.97rem] leading-6' : 'px-5 md:px-6 py-5 text-[1.08rem] leading-8'} text-slate-400 transition-all duration-350 ${
                    isTransitioning ? 'opacity-0 translate-y-[-4px]' : 'opacity-100 translate-y-0'
                  }`}
                  style={{ transitionDuration: '350ms' }}
                >
                  e.g., {displayedPlaceholder}
                </div>
              )}
              <textarea
                ref={textareaRef}
                autoFocus={!isMobile}
                value={researchQuery}
                onChange={event => onResearchQueryChange(event.target.value)}
                placeholder=""
                maxLength={1500}
                rows={isMobile ? 2 : 3}
                className={`w-full resize-none rounded-[22px] bg-transparent ${
                  isMobile ? 'px-4 py-3.5 text-[0.97rem] leading-6' : 'px-5 md:px-6 py-5 text-[1.08rem] leading-8'
                } text-slate-900 focus:outline-none`}
              />
              <div className={`flex items-center justify-between ${isMobile ? 'px-4 pb-3 text-[11px]' : 'px-5 md:px-6 pb-4 text-xs'} text-slate-400`}>
                <span>{researchQuery.length}/1500</span>
                {researchQuery.length > 1300 && <span>Narrow the scope for best results</span>}
              </div>
            </div>
          </div>
        </div>

        <div className={isMobile ? 'space-y-2' : 'space-y-3'}>
          <p className="hero-field-label">Visibility</p>
          <div className="hero-visibility-toggle inline-flex items-center rounded-full border border-slate-300 bg-white p-1">
            <button
              type="button"
              onClick={onSelectPrivate}
              className={`rounded-full font-medium transition ${isMobile ? 'px-3 py-1.5 text-[0.92rem]' : 'px-4 py-2 text-sm'} ${
                !isPublic ? 'bg-black text-white shadow-none' : 'bg-transparent text-slate-900'
              }`}
            >
              Private
            </button>
            <button
              type="button"
              onClick={onSelectPublic}
              className={`rounded-full font-medium transition ${isMobile ? 'px-3 py-1.5 text-[0.92rem]' : 'px-4 py-2 text-sm'} ${
                isPublic ? 'bg-black text-white shadow-none' : 'bg-transparent text-slate-900'
              }`}
            >
              Public
            </button>
          </div>
          <p className="hero-field-helper">{visibilityHelperCopy}</p>
        </div>

        <div className={`${isMobile ? 'pt-1' : 'pt-3'} border-t border-slate-200`}>
          <div className={`hero-cta-shell ${isMobile ? 'pt-0' : 'pt-0'}`}>
            <button
              type="button"
              onClick={onStartResearch}
              disabled={!queryValid}
              aria-describedby="landing-start-research-hint"
              className={`w-full rounded-2xl bg-black px-4 text-sm font-medium text-white transition-all duration-200 ${
                isMobile ? 'py-3' : 'py-4'
              } ${!queryValid ? 'cursor-not-allowed opacity-50' : 'shadow-[0_10px_30px_rgba(15,23,42,0.12)] hover:bg-slate-900'}`}
            >
              <span className="inline-flex items-center gap-2">
                <span>Start Research</span>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </button>
            <p id="landing-start-research-hint" className={`text-left ${isMobile ? 'mt-2.5 text-[10px] leading-4' : 'mt-3 text-xs'} text-slate-500`}>
              {queryValid
                ? 'Sign in to launch your 45–75 minute report.'
                : 'Enter at least 10 characters to launch a 45–75 minute report.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <nav
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${navSurfaceClass}`}
        data-testid="landing-nav"
      >
        <div className={`mx-auto flex w-full max-w-[84rem] items-center justify-between ${isMobile ? 'px-4 py-3.5' : 'px-6 py-5'}`}>
          <span
            className={`font-semibold tracking-[-0.03em] ${
              isMobile ? 'text-lg' : 'text-[1.16rem]'
            } ${isInDarkSection && isScrolled ? 'text-white' : 'text-slate-900'}`}
          >
            Insight Atlas
          </span>

          <div className="hidden items-center gap-8 md:flex">
            <button className={`text-[0.92rem] transition-colors ${navTextClass}`} onClick={onHome}>
              Home
            </button>
            <button className={`text-[0.92rem] transition-colors ${navTextClass}`} onClick={onLogin}>
              Login
            </button>
            <button
              onClick={onGetStarted}
              className="rounded-2xl bg-black px-5 py-3 text-[0.92rem] font-medium text-white shadow-[0_8px_24px_rgba(15,23,42,0.12)] transition hover:bg-slate-900"
            >
              Get Started
            </button>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button className={`px-3 py-2 text-sm transition-colors ${navTextClass}`} onClick={onLogin}>
              Login
            </button>
            <button
              onClick={onGetStarted}
              className="rounded-[14px] bg-black px-4 py-2 text-sm font-medium text-white shadow-[0_8px_24px_rgba(15,23,42,0.12)] transition hover:bg-slate-900"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      <section
        className={`snap-hero relative flex flex-col bg-[#e9def8] ${isMobile ? 'pb-4' : 'pt-16'}`}
        style={isMobile ? { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' } : undefined}
      >
        <HeroBackground />

        <div className={`hero-shell-frame relative z-10 mx-auto flex w-full flex-1 flex-col min-h-0 animate-fade-in ${isMobile ? 'hero-shell-frame--mobile px-4 pt-4' : 'px-6 pt-[5.25rem]'}`}>
          <div className={`hero-shell ${isMobile ? 'hero-shell--mobile' : 'mb-4'}`}>
            {isMobile ? (
              <>
                <div className="hero-mobile-heading-wrap px-4 pt-4 pb-3">
                  <h1 className="landing-hero-headline text-[clamp(1.95rem,9vw,2.18rem)] font-bold leading-[0.98] tracking-[-0.055em] text-slate-950">
                    Deep research,
                    <span className="block">packaged like a product.</span>
                  </h1>
                </div>
                {heroActionPane}
                <div className="border-t border-slate-200/80 p-4">{heroResearchCards}</div>
              </>
            ) : (
              <div className="hero-split-grid">
                <div className="px-8 pb-8 pt-8 lg:px-10 lg:pb-10 lg:pt-10 xl:px-12 xl:pb-12 xl:pt-12">
                  <h1 className="landing-hero-headline text-4xl font-bold leading-[0.98] tracking-[-0.055em] text-slate-950 lg:text-[3.1rem] xl:text-[3.35rem]">
                    Deep research,
                    <span className="block">packaged like a product.</span>
                  </h1>
                  {heroResearchCards}
                </div>
                {heroActionPane}
              </div>
            )}
          </div>

          <div className={`hero-scroll-hint flex flex-shrink-0 flex-col items-center gap-1.5 ${isMobile ? 'hero-scroll-hint--mobile pt-1 pb-1' : 'pt-2 pb-1'}`}>
            <p className="text-xs text-slate-950">Explore core features below</p>
            <svg className="h-4 w-4 animate-bounce text-slate-950" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>
    </>
  )
}
